<template>
  <div class="main-certificate-page">
    <!-- ===== Hero / Summary Header ===== -->
    <b-card
      no-body
      class="hero-card mb-1 overflow-hidden"
    >
      <div class="hero-inner">
        <div class="hero-text">
          <div class="hero-badge">
            <feather-icon
              icon="AwardIcon"
              size="18"
            />
            <span>Certificates Directory</span>
          </div>
          <h2 class="hero-title">
            Main Certificates
          </h2>
          <p class="hero-subtitle">
            Browse all certification programs grouped by their issuing source, and
            explore the engineers assigned to each certificate.
          </p>
        </div>

        <div class="hero-stats">
          <div class="stat-pill">
            <div class="stat-icon stat-icon--primary">
              <feather-icon
                icon="FileTextIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ totalCertificates }}
              </div>
              <div class="stat-label">
                Certificates
              </div>
            </div>
          </div>

          <div class="stat-pill">
            <div class="stat-icon stat-icon--info">
              <feather-icon
                icon="LayersIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ sourcesCount }}
              </div>
              <div class="stat-label">
                Sources
              </div>
            </div>
          </div>

          <div class="stat-pill">
            <div class="stat-icon stat-icon--success">
              <feather-icon
                icon="UsersIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ uniqueEngineersCount }}
              </div>
              <div class="stat-label">
                Engineers
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- ===== Toolbar : Search + Source filters ===== -->
    <b-card
      no-body
      class="toolbar-card mb-1"
    >
      <div class="toolbar-inner">
        <b-input-group class="search-group">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="search"
            placeholder="Search by certificate name or engineer..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="search"
            is-text
            class="clear-search"
            @click.native="search = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>

        <div class="source-chips">
          <button
            type="button"
            class="source-chip"
            :class="{ 'source-chip--active': activeSource === 'all' }"
            @click="activeSource = 'all'"
          >
            <feather-icon
              icon="GridIcon"
              size="14"
            />
            <span>All</span>
          </button>
          <button
            v-for="src in availableSources"
            :key="src"
            type="button"
            class="source-chip"
            :class="{ 'source-chip--active': activeSource === src }"
            :style="activeSource === src ? chipActiveStyle(src) : {}"
            @click="activeSource = src"
          >
            <feather-icon
              :icon="sourceMeta(src).icon"
              size="14"
            />
            <span>{{ src }}</span>
          </button>
        </div>
      </div>
    </b-card>

    <!-- ===== Loading ===== -->
    <div
      v-if="dataLoad"
      class="text-center my-5 py-5"
    >
      <b-spinner
        type="grow"
        variant="primary"
        label="Loading..."
      />
      <p class="mt-1 text-muted">
        Loading certificates...
      </p>
    </div>

    <!-- ===== Empty state ===== -->
    <b-card
      v-else-if="!hasResults"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No certificates found</h4>
      <p class="text-muted mb-0">
        Try adjusting your search or filter.
      </p>
    </b-card>

    <!-- ===== Content : grouped by source ===== -->
    <div
      v-else
      class="groups-wrapper"
    >
      <div
        v-for="(items, source) in filteredGroups"
        :key="source"
        class="source-group"
      >
        <!-- Group header -->
        <div
          class="group-header"
          :style="{ background: sourceMeta(source).gradient }"
        >
          <div class="group-header-left">
            <div class="group-header-icon">
              <feather-icon
                :icon="sourceMeta(source).icon"
                size="22"
              />
            </div>
            <div>
              <h3 class="group-title">
                {{ source }}
              </h3>
              <span class="group-sub">Issuing Source</span>
            </div>
          </div>
          <b-badge
            pill
            class="group-count"
          >
            {{ items.length }} {{ items.length === 1 ? 'certificate' : 'certificates' }}
          </b-badge>
        </div>

        <!-- Cards -->
        <b-row class="mt-50">
          <b-col
            v-for="item in items"
            :key="item.id"
            cols="12"
            md="6"
            xl="4"
          >
            <div class="cert-card">
              <div
                class="cert-accent"
                :style="{ background: sourceMeta(source).gradient }"
              />

              <div class="cert-body">
                <div class="cert-top">
                  <div
                    class="cert-source-icon"
                    :style="{ background: sourceMeta(source).soft, color: sourceMeta(source).color }"
                  >
                    <feather-icon
                      :icon="sourceMeta(source).icon"
                      size="20"
                    />
                  </div>
                  <b-badge
                    variant="light-secondary"
                    class="cert-id"
                  >
                    #{{ item.id }}
                  </b-badge>
                </div>

                <h4
                  class="cert-name-en"
                  :title="item.name && item.name.en"
                >
                  {{ (item.name && item.name.en) || '—' }}
                </h4>
                <p
                  class="cert-name-ar"
                  dir="rtl"
                >
                  {{ (item.name && item.name.ar) || '—' }}
                </p>

                <!-- Engineers -->
                <div class="cert-engineers">
                  <div class="cert-engineers-head">
                    <feather-icon
                      icon="UsersIcon"
                      size="14"
                      class="mr-25"
                    />
                    <span>Engineers</span>
                    <b-badge
                      pill
                      variant="light-primary"
                      class="ml-50"
                    >
                      {{ engineerList(item.engineer_names).length }}
                    </b-badge>
                  </div>

                  <div
                    v-if="engineerList(item.engineer_names).length"
                    class="avatar-stack"
                    @click="ApprovalRow(item.engineer_names, item.name && item.name.en)"
                  >
                    <div
                      v-for="(name, idx) in engineerList(item.engineer_names).slice(0, 5)"
                      :key="idx"
                      class="avatar-chip"
                      :style="{ background: avatarColor(name) }"
                      :title="name"
                    >
                      {{ initials(name) }}
                    </div>
                    <div
                      v-if="engineerList(item.engineer_names).length > 5"
                      class="avatar-chip avatar-more"
                    >
                      +{{ engineerList(item.engineer_names).length - 5 }}
                    </div>
                  </div>
                  <div
                    v-else
                    class="no-engineers"
                  >
                    <feather-icon
                      icon="UserXIcon"
                      size="14"
                      class="mr-25"
                    />
                    No engineers assigned
                  </div>
                </div>
              </div>

              <div class="cert-footer">
                <button
                  type="button"
                  class="footer-btn footer-btn--outline"
                  :disabled="!engineerList(item.engineer_names).length"
                  :style="{
                    '--btn-gradient': sourceMeta(source).gradient,
                    '--btn-color': sourceMeta(source).color,
                  }"
                  @click="ApprovalRow(item.engineer_names, item.name && item.name.en)"
                >
                  <feather-icon
                    icon="ListIcon"
                    size="15"
                    class="mr-25"
                  />
                  Engineers
                </button>
                <button
                  type="button"
                  class="footer-btn footer-btn--soft"
                  :style="{
                    '--btn-gradient': sourceMeta(source).gradient,
                    '--btn-color': sourceMeta(source).color,
                  }"
                  @click="$router.push({
                    name: 'certificate',
                    params: { MCR_id: item.id, Certificate_Name: item.name.en }
                  })"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="15"
                    class="mr-25"
                  />
                  View Details
                </button>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BSpinner, BBadge,
  BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import DOMPurify from 'dompurify'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BBadge,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
  },
  data() {
    return {
      dataLoad: false,
      totalRows: '',
      search: '',
      activeSource: 'all',
      categorizedItems: {},
    }
  },
  computed: {
    availableSources() {
      return Object.keys(this.categorizedItems)
    },
    sourcesCount() {
      return this.availableSources.length
    },
    totalCertificates() {
      return this.availableSources.reduce(
        (acc, src) => acc + this.categorizedItems[src].length,
        0,
      )
    },
    uniqueEngineersCount() {
      const set = new Set()
      this.availableSources.forEach(src => {
        this.categorizedItems[src].forEach(item => {
          this.engineerList(item.engineer_names).forEach(n => set.add(n.toLowerCase()))
        })
      })
      return set.size
    },
    filteredGroups() {
      const term = this.search.trim().toLowerCase()
      const result = {}
      this.availableSources
        .filter(src => this.activeSource === 'all' || this.activeSource === src)
        .forEach(src => {
          const items = this.categorizedItems[src].filter(item => {
            if (!term) return true
            const en = (item.name && item.name.en ? item.name.en : '').toLowerCase()
            const ar = (item.name && item.name.ar ? item.name.ar : '').toLowerCase()
            const eng = (item.engineer_names || '').toLowerCase()
            return en.includes(term) || ar.includes(term) || eng.includes(term)
          })
          if (items.length) result[src] = items
        })
      return result
    },
    hasResults() {
      return Object.keys(this.filteredGroups).length > 0
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    sourceMeta(source) {
      const map = {
        // SFDA (Saudi Food & Drug Authority) - brand teal-green, regulatory shield
        SFDA: {
          icon: 'ShieldIcon', color: '#0a8f7a', soft: 'rgba(10,143,122,0.12)', gradient: 'linear-gradient(135deg, #12b39a, #066a5b)',
        },
        // SFDA-EG - same SFDA family, shifted to a deeper cyan-blue so it stays distinguishable
        'SFDA-EG': {
          icon: 'ShieldIcon', color: '#0e7fa6', soft: 'rgba(14,127,166,0.12)', gradient: 'linear-gradient(135deg, #27a9d6, #0a5878)',
        },
        // Jeem (SASO - Saudi Standards) - logo green -> blue, conformity check
        Jeem: {
          icon: 'CheckCircleIcon', color: '#2a8f9f', soft: 'rgba(42,143,159,0.13)', gradient: 'linear-gradient(135deg, #6bbf4a, #1c8fb0)',
        },
        // Saber (SASO) - Saudi green, product conformity package
        Saber: {
          icon: 'PackageIcon', color: '#1b7f3b', soft: 'rgba(27,127,59,0.13)', gradient: 'linear-gradient(135deg, #2fa356, #0f5a26)',
        },
        // Maneh (SASO portal) - site palette: primary #1b8354, light #54c08a, dark #074d31
        Maneh: {
          icon: 'LockIcon', color: '#1b8354', soft: 'rgba(27,131,84,0.12)', gradient: 'linear-gradient(135deg, #54c08a, #1b8354 55%, #074d31)',
        },
        // MOIAT (UAE) - logo gold #BC9329 + bronze #92722A, industry
        Moiat: {
          icon: 'ToolIcon', color: '#92722A', soft: 'rgba(188,147,41,0.15)', gradient: 'linear-gradient(135deg, #BC9329, #92722A)',
        },
        // SLS (Sri Lanka Standards) - saffron
        SLS: {
          icon: 'AwardIcon', color: '#ef6c00', soft: 'rgba(239,108,0,0.13)', gradient: 'linear-gradient(135deg, #ff9a2e, #c24e00)',
        },
        // GOEIC (Egypt import/export control) - Egyptian gold, shipments
        GOEIC: {
          icon: 'TruckIcon', color: '#b8860b', soft: 'rgba(184,134,11,0.14)', gradient: 'linear-gradient(135deg, #d9a82b, #8a6208)',
        },
        // MOPH (Ministry of Public Health) - maroon, health
        MOPH: {
          icon: 'HeartIcon', color: '#8a1538', soft: 'rgba(138,21,56,0.12)', gradient: 'linear-gradient(135deg, #b02456, #5c0b24)',
        },
        // Halal - leaf green, crescent
        Halal: {
          icon: 'MoonIcon', color: '#5b9a14', soft: 'rgba(91,154,20,0.14)', gradient: 'linear-gradient(135deg, #8bc34a, #4a7a0e)',
        },
        ISO: {
          icon: 'HexagonIcon', color: '#2f3fa8', soft: 'rgba(47,63,168,0.12)', gradient: 'linear-gradient(135deg, #4a5bd6, #1b2570)',
        },
      }
      return map[source] || {
        icon: 'FileTextIcon',
        color: '#6c757d',
        soft: 'rgba(108,117,125,0.14)',
        gradient: 'linear-gradient(135deg, #8a94a6, #3b4252)',
      }
    },
    chipActiveStyle(source) {
      const meta = this.sourceMeta(source)
      return {
        background: meta.gradient,
        borderColor: 'transparent',
        color: '#fff',
        boxShadow: `0 6px 16px -6px ${meta.color}`,
      }
    },
    engineerList(names) {
      if (!names) return []
      return names
        .split(',')
        .map(n => n.trim())
        .filter(Boolean)
    },
    initials(name) {
      const parts = name.trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },
    avatarColor(name) {
      const palette = [
        'linear-gradient(135deg, #667eea, #764ba2)',
        'linear-gradient(135deg, #f093fb, #f5576c)',
        'linear-gradient(135deg, #4facfe, #00f2fe)',
        'linear-gradient(135deg, #43e97b, #38f9d7)',
        'linear-gradient(135deg, #fa709a, #fee140)',
        'linear-gradient(135deg, #30cfd0, #330867)',
        'linear-gradient(135deg, #a8edea, #fed6e3)',
        'linear-gradient(135deg, #ff9a9e, #fad0c4)',
        'linear-gradient(135deg, #f6d365, #fda085)',
        'linear-gradient(135deg, #5ee7df, #b490ca)',
      ]
      let hash = 0
      for (let i = 0; i < name.length; i += 1) {
        hash = (hash * 31 + name.charCodeAt(i)) % 1000000007
      }
      return palette[Math.abs(hash) % palette.length]
    },
    formatEngineerNames(names) {
      const clean = DOMPurify.sanitize(names, { ALLOWED_TAGS: [] })
      const list = clean.split(',').map(n => n.trim()).filter(Boolean)
      return `
        <div style="max-height: 55vh; overflow-y: auto; padding: 4px;">
          <table style="width: 100%; border-collapse: collapse; margin: auto;">
            <thead>
              <tr style="position: sticky; top: 0; background: #f3f2f7;">
                <th style="border-bottom: 2px solid #e0e0e0; padding: 10px; text-align: center; width: 48px;">#</th>
                <th style="border-bottom: 2px solid #e0e0e0; padding: 10px; text-align: left;">Engineer Name</th>
              </tr>
            </thead>
            <tbody>
              ${list.map((name, index) => `
                <tr style="background: ${index % 2 === 0 ? '#ffffff' : '#fafafa'};">
                  <td style="border-bottom: 1px solid #eee; padding: 8px 10px; text-align: center; color: #7367f0; font-weight: 600;">${index + 1}</td>
                  <td style="border-bottom: 1px solid #eee; padding: 8px 10px; text-align: left;">${name}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        </div>
      `
    },
    ApprovalRow(names, certName) {
      if (!this.engineerList(names).length) return
      const getName = String.raw`${names}`
      this.$swal({
        title: certName ? `Engineers · ${certName}` : 'Engineer List',
        html: this.formatEngineerNames(getName),
        width: 620,
        showCancelButton: false,
        confirmButtonColor: '#7367f0',
        confirmButtonText: 'Close',
      })
    },
    async getData() {
      this.dataLoad = true
      try {
        const response = await axios.get('certificate_group')

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data[Object.keys(responseData?.data)[0]] || []
          const sources = [...new Set(data.map(item => item.source))]

          const grouped = {}
          sources.forEach(source => {
            grouped[source] = data.filter(item => item.source === source)
          })
          this.categorizedItems = grouped
          this.totalRows = responseData.data.count || data.length
        }
      } catch (error) {
        if (error.response && error.response.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server Error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.dataLoad = false
      }
    },
  },
}
</script>

<style lang="scss" scoped src="./MainCertificate.scss"></style>
